import axios from 'axios';
import { SYSTEMDOX_API_URL } from '@/lib/apiConfig';

const api = axios.create({ baseURL: SYSTEMDOX_API_URL });

export interface StalenessResult {
  analyzed: boolean;
  staleness_score?: number;
  stale_sections?: { heading: string; reason: string; confidence: string }[];
  suggested_updates?: string[];
  summary?: string;
  analyzed_at?: string;
}

export interface StalenessReportItem {
  doc_id: string;
  path: string;
  title: string;
  doc_type: string;
  repo: string;
  staleness_score: number | null;
  summary: string;
  analyzed_at: string | null;
}

/** A finished-but-unarchived change (Documentation Standard §3.3) — its
 *  tasks are done (or only Post-implementation graduation items remain)
 *  yet the folder still sits in specs/changes/. */
export interface UnarchivedChange {
  repo: string;
  slug: string;
  path: string;
  taskDone: number;
  taskTotal: number;
  pendingPostImplOnly: boolean;
  lastCommitAt: string;
  daysSinceLastCommit: number | null;
  stale: boolean;
}

export const stalenessApi = {
  setAuthProvider(getToken: () => Promise<string | null>) {
    api.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  },

  async analyzeStaleness(docId: string): Promise<StalenessResult> {
    const { data } = await api.post(`/docs/${encodeURIComponent(docId)}/analyze-staleness`);
    return { analyzed: true, ...data };
  },

  async getStaleness(docId: string): Promise<StalenessResult> {
    const { data } = await api.get(`/docs/${encodeURIComponent(docId)}/staleness`);
    return data;
  },

  async getReport(repo?: string, project?: string): Promise<StalenessReportItem[]> {
    const { data } = await api.get('/staleness/report', { params: { ...(repo && { repo }), ...(project && { project }) } });
    return data;
  },

  async getUnarchivedChanges(): Promise<{ changes: UnarchivedChange[]; thresholdDays: number }> {
    const { data } = await api.get('/staleness/unarchived-changes');
    return data;
  },
};
